import {
  isUsdSiteCurrency,
  resolveSiteCurrency,
  resolveSiteName
} from '@/utils/siteBrand'

const STORAGE_PREFIX = 'helper.jackpot.v3'
const KRW_JACKPOT_MIN = 10000000
const KRW_JACKPOT_MAX = 100000000

function randomInt (min, max) {
  return min + Math.floor(Math.random() * (max - min + 1))
}

/** 끝자리를 난수로 바꿔 연속 숫자(1234567 등)처럼 보이지 않게 */
function withRandomTail (base, tailDigits) {
  const divisor = Math.pow(10, tailDigits)
  const tail = randomInt(1, divisor - 1)
  return Math.floor(base / divisor) * divisor + tail
}

function generateKrwJackpotAmount () {
  return withRandomTail(randomInt(10000000, 15000000), 2)
}

function generateKrwJackpotResetAmount () {
  return withRandomTail(randomInt(10000000, 12500000), 2)
}

function storageKey (siteInfo) {
  const host = typeof window !== 'undefined' ? window.location.hostname : 'default'
  const site = resolveSiteName(siteInfo)
  const currency = resolveSiteCurrency(siteInfo)
  return `${STORAGE_PREFIX}.${host}.${site}.${currency}`
}

function readStored (key) {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(key)
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function writeStored (key, amount) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(key, String(amount))
  } catch (e) { /* ignore */ }
}

function generateAmount (siteInfo) {
  if (isUsdSiteCurrency(siteInfo)) {
    // 만 달러 준위: 약 $42,000 ~ $285,000
    const thousands = randomInt(42, 285)
    const tail = randomInt(1, 999)
    return thousands * 1000 + tail
  }
  return generateKrwJackpotAmount()
}

function normalizeJackpotAmount (siteInfo, amount) {
  const num = Math.floor(Number(amount) || 0)
  if (isUsdSiteCurrency(siteInfo)) {
    return num > 0 ? num : generateAmount(siteInfo)
  }
  if (num <= 0 || num > KRW_JACKPOT_MAX) {
    return generateKrwJackpotResetAmount()
  }
  if (num < KRW_JACKPOT_MIN) {
    return generateKrwJackpotAmount()
  }
  return num
}

/** KRW: 1억 초과 시 약 1천만대로 리셋 */
export function getJackpotTickTarget (siteInfo, currentAmount, delta) {
  const start = Math.floor(Number(currentAmount) || 0)
  const step = Math.floor(Number(delta) || 0)
  if (isUsdSiteCurrency(siteInfo)) {
    return {
      end: start + step,
      rollover: false,
      resetAmount: null
    }
  }
  const next = start + step
  if (next > KRW_JACKPOT_MAX) {
    return {
      end: KRW_JACKPOT_MAX,
      rollover: true,
      resetAmount: generateKrwJackpotResetAmount()
    }
  }
  return {
    end: next,
    rollover: false,
    resetAmount: null
  }
}

/** 잭팟 표시 금액 (표시 단위 그대로 저장) */
export function createJackpotDisplayAmount (siteInfo) {
  const key = storageKey(siteInfo)
  const cached = readStored(key)
  if (cached != null) {
    const amount = normalizeJackpotAmount(siteInfo, cached)
    if (amount !== cached) writeStored(key, amount)
    return amount
  }

  const amount = generateAmount(siteInfo)
  writeStored(key, amount)
  return amount
}

export function persistJackpotDisplayAmount (siteInfo, amount) {
  if (!siteInfo) return
  const num = normalizeJackpotAmount(siteInfo, amount)
  if (num <= 0) return
  writeStored(storageKey(siteInfo), num)
}

export function formatJackpotAmount (value) {
  const num = Math.floor(Number(value) || 0)
  return num.toLocaleString('ko-KR')
}

export function getJackpotTickStep (siteInfo) {
  return getJackpotTickDelta(siteInfo)
}

/** 한 번에 올라갈 금액 (KRW: 1,000~3,000 랜덤) */
export function getJackpotTickDelta (siteInfo) {
  if (isUsdSiteCurrency(siteInfo)) return randomInt(5, 15)
  return randomInt(1000, 3000)
}

/** 증가 애니메이션 재생 시간(ms) */
export function getJackpotTickAnimationMs (siteInfo) {
  if (isUsdSiteCurrency(siteInfo)) return 900 + Math.floor(Math.random() * 500)
  return 1100 + Math.floor(Math.random() * 900)
}

/** 증가 사이 대기 시간(ms) */
export function getJackpotTickPauseMs () {
  return 3000
}

export function getJackpotTickDelayMs (siteInfo) {
  return getJackpotTickPauseMs(siteInfo)
}
